/**
 * Derive Sitewide -> Client Info defaults from a loaded website template.
 *
 * Template source profiles are useful but intentionally thin. This module
 * combines those profiles with the actual loaded section HTML so the client
 * panel starts with the best available contact, address, domain, social, and
 * brand text values.
 */

import { normalizeClientInfo } from './client-info.js';

function cleanString(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function stripProtocol(value) {
  return cleanString(value)
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/g, '');
}

function normalizeComparable(value) {
  return cleanString(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function titleCaseWords(value) {
  return cleanString(value)
    .replace(/[-_]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function domainLabel(domain) {
  const host = stripProtocol(domain).split('/')[0] || '';
  return host.split('.')[0] || host;
}

function humanizeDomain(domain) {
  return titleCaseWords(domainLabel(domain));
}

function cleanEmail(value) {
  const raw = cleanString(value).replace(/^mailto:/i, '');
  return raw.split('?')[0].trim();
}

function phoneDigits(value) {
  return cleanString(value).replace(/\D+/g, '');
}

function formatUSPhone(digits) {
  if (!/^\d{10}$/.test(digits)) return '';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isFormattedPhone(value) {
  const raw = cleanString(value);
  return raw && /[()\s.-]/.test(raw) && phoneDigits(raw).length >= 7;
}

function parseURL(value) {
  const raw = cleanString(value);
  if (!raw || raw.startsWith('#') || raw.startsWith('mailto:') || raw.startsWith('tel:'))
    return null;
  try {
    return new URL(raw, 'https://example.invalid/');
  } catch {
    return null;
  }
}

function isMapURL(value) {
  const parsed = parseURL(value);
  if (!parsed) return false;
  const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
  return (
    (host.includes('google.com') && parsed.pathname.toLowerCase().includes('/maps')) ||
    host === 'maps.app.goo.gl' ||
    host === 'goo.gl' ||
    (host.includes('bing.com') && parsed.pathname.toLowerCase().includes('maps')) ||
    host === 'maps.apple.com' ||
    host.includes('mapquest.') ||
    host.includes('waze.com')
  );
}

function socialKeyForURL(value) {
  const parsed = parseURL(value);
  if (!parsed) return '';
  const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
  const path = parsed.pathname.replace(/^\/+|\/+$/g, '');
  if (!path && !['youtu.be'].includes(host)) return '';
  if (host === 'facebook.com' || host.endsWith('.facebook.com')) return 'facebook';
  if (host === 'instagram.com' || host.endsWith('.instagram.com')) return 'instagram';
  if (host === 'linkedin.com' || host.endsWith('.linkedin.com')) return 'linkedin';
  if (host === 'x.com' || host === 'twitter.com') return 'x';
  if (host === 'youtube.com' || host.endsWith('.youtube.com') || host === 'youtu.be')
    return 'youtube';
  if (host === 'yelp.com' || host.endsWith('.yelp.com')) return 'yelp';
  return '';
}

function parseRoots(sections) {
  if (typeof DOMParser === 'undefined') return [];
  const parser = new DOMParser();
  return (Array.isArray(sections) ? sections : [])
    .map((section) => {
      const html = typeof section?.html === 'string' ? section.html : '';
      if (!html.trim()) return null;
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      const root = doc.body.firstElementChild;
      return root ? { section, root } : null;
    })
    .filter(Boolean);
}

function collectCandidateText(root) {
  const candidates = [];
  const selectors = [
    '.cs-topper',
    '.cs-logo',
    '[class*="brand"]',
    '[class*="logo"]',
    'h1',
    'h2',
    '[aria-label]',
    '[alt]',
    '[title]'
  ].join(',');
  for (const el of Array.from(root.querySelectorAll(selectors))) {
    const values = [
      el.textContent,
      el.getAttribute('aria-label'),
      el.getAttribute('alt'),
      el.getAttribute('title')
    ];
    for (const value of values) {
      const text = cleanString(value);
      if (text && text.length <= 80) candidates.push(text);
    }
  }
  return candidates;
}

function profileNameCandidates(sourceProfile, siteName) {
  const variants = Array.isArray(sourceProfile?.businessNameVariants)
    ? sourceProfile.businessNameVariants
    : [];
  return [
    sourceProfile?.businessName,
    ...variants,
    humanizeDomain(sourceProfile?.sourceDomain || siteName)
  ]
    .map(cleanString)
    .filter(Boolean);
}

function chooseProfileFallbackName(sourceProfile, siteName) {
  const candidates = profileNameCandidates(sourceProfile, siteName);
  const domainNorm = normalizeComparable(domainLabel(sourceProfile?.sourceDomain || siteName));
  const domainVariant = candidates.find(
    (candidate) => normalizeComparable(candidate) === domainNorm
  );
  if (domainVariant) return domainVariant;
  return candidates[0] || humanizeDomain(siteName);
}

function inferBusinessName(sourceProfile, roots, siteName) {
  const profileCandidates = profileNameCandidates(sourceProfile, siteName);
  const profileNorms = new Set(profileCandidates.map(normalizeComparable).filter(Boolean));
  let best = '';
  let bestScore = -1;

  for (const { root } of roots) {
    for (const candidate of collectCandidateText(root)) {
      const norm = normalizeComparable(candidate);
      if (!norm || !profileNorms.has(norm)) continue;
      let score = 10;
      if (/\s/.test(candidate)) score += 5;
      if (/logo/i.test(candidate)) score -= 3;
      if (candidate.length < 40) score += 2;
      if (score > bestScore) {
        best = candidate.replace(/\s+logo$/i, '').trim();
        bestScore = score;
      }
    }
  }

  return best || chooseProfileFallbackName(sourceProfile, siteName);
}

function inferTagline(sourceProfile, roots, businessName) {
  const direct = cleanString(sourceProfile?.tagline);
  if (direct) return direct;
  const nameNorm = normalizeComparable(businessName);
  const hero = roots.find(
    ({ section }) =>
      section?.category === 'hero' ||
      /(^|[-~])hero(?:[-~]|$)/i.test(section?.templateSectionId || '') ||
      /(^|[-~])hero(?:[-~]|$)/i.test(section?.sectionId || '')
  );
  const h1 = cleanString(hero?.root?.querySelector('h1')?.textContent);
  if (h1 && normalizeComparable(h1) !== nameNorm) return h1;
  return '';
}

function parseProfileAddress(lines) {
  const values = (Array.isArray(lines) ? lines : []).map(cleanString).filter(Boolean);
  if (values.length < 4) return null;
  const hasLineTwo = values.length >= 5;
  return {
    lineOne: values[0] || '',
    lineTwo: hasLineTwo ? values[1] || '' : '',
    city: hasLineTwo ? values[values.length - 3] || '' : values[1] || '',
    county: values[values.length - 2] || '',
    postcode: values[values.length - 1] || '',
    mapLink: ''
  };
}

function parseAddressText(value) {
  const text = cleanString(value).replace(/\s*,\s*/g, ', ');
  if (!/\d/.test(text)) return null;
  const parts = text.split(',').map(cleanString).filter(Boolean);
  if (parts.length < 2) return null;
  const lineOne = parts[0];
  const rest = parts.slice(1).join(' ');
  const match = rest.match(
    /^(.+?)\s+([A-Z]{2}|[A-Za-z]{2,})\s+([A-Z]?\d[A-Z\d]?\s*\d[A-Z]{2}|\d{5}(?:-\d{4})?)$/i
  );
  if (!match) return { lineOne, lineTwo: '', city: rest, county: '', postcode: '', mapLink: '' };
  return {
    lineOne,
    lineTwo: '',
    city: cleanString(match[1]),
    county: cleanString(match[2]).toUpperCase(),
    postcode: cleanString(match[3]),
    mapLink: ''
  };
}

function scanLinks(roots) {
  const links = [];
  for (const { root } of roots) {
    for (const a of Array.from(root.querySelectorAll('a[href]'))) {
      links.push({
        href: cleanString(a.getAttribute('href')),
        text: cleanString(a.textContent)
      });
    }
  }
  return links;
}

function inferSocialLinks(links) {
  const social = {};
  for (const { href } of links) {
    const key = socialKeyForURL(href);
    if (key && !social[key]) social[key] = href;
  }
  return social;
}

function inferMapLink(links) {
  return links.find((link) => isMapURL(link.href))?.href || '';
}

function inferAddress(links, sourceProfile) {
  const profileAddress = parseProfileAddress(sourceProfile?.addressLines);
  const mapLink = inferMapLink(links);
  const mapAddress = links
    .filter((link) => isMapURL(link.href))
    .map((link) => parseAddressText(link.text))
    .find(Boolean);
  return {
    lineOne: profileAddress?.lineOne || mapAddress?.lineOne || '',
    lineTwo: profileAddress?.lineTwo || mapAddress?.lineTwo || '',
    city: profileAddress?.city || mapAddress?.city || '',
    county: profileAddress?.county || mapAddress?.county || '',
    postcode: profileAddress?.postcode || mapAddress?.postcode || '',
    mapLink
  };
}

function inferEmail(links, roots, sourceProfile) {
  const profileEmail = cleanEmail(sourceProfile?.email);
  if (profileEmail) return profileEmail;
  const mailto = links.find((link) => /^mailto:/i.test(link.href) && cleanEmail(link.href));
  if (mailto) return cleanEmail(mailto.href);
  for (const { root } of roots) {
    const match = cleanString(root.textContent).match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (match) return match[0];
  }
  return '';
}

function inferPhone(sourceProfile, links) {
  const profilePhones = (
    Array.isArray(sourceProfile?.phoneVariants) ? sourceProfile.phoneVariants : []
  )
    .map(cleanString)
    .filter((phone) => phoneDigits(phone).length >= 7);
  const telLinks = links
    .filter((link) => /^tel:/i.test(link.href))
    .map((link) => cleanString(link.text) || cleanString(link.href).replace(/^tel:/i, ''))
    .filter((phone) => phoneDigits(phone).length >= 7);
  const phones = [...profilePhones, ...telLinks];
  const raw = phones[0] || '';
  const digits = phoneDigits(raw);
  const formatted = phones.find(isFormattedPhone) || formatUSPhone(digits) || raw;
  return { phone: digits, phoneFormatted: formatted };
}

export function deriveClientInfoFromTemplate({
  siteName = '',
  sourceProfile = {},
  sections = [],
  projectName = 'Untitled'
} = {}) {
  const roots = parseRoots(sections);
  const links = scanLinks(roots);
  const domain = stripProtocol(sourceProfile?.sourceDomain || siteName);
  const name = inferBusinessName(sourceProfile, roots, siteName);
  const phone = inferPhone(sourceProfile, links);
  const address = inferAddress(links, sourceProfile);

  return normalizeClientInfo(
    {
      name,
      tagline: inferTagline(sourceProfile, roots, name),
      email: inferEmail(links, roots, sourceProfile),
      phone: phone.phone,
      phoneFormatted: phone.phoneFormatted,
      domain,
      address,
      social: inferSocialLinks(links)
    },
    projectName || name || humanizeDomain(domain || siteName)
  );
}
